import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowDown,
  ArrowRight,
  ChevronRight,
  GripVertical,
  Loader2,
  MoreHorizontal,
  Plus,
  Trash2,
  Trophy,
  X,
  XCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TAG_COLORS, TAG_COLOR_KEYS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { getProject, updateProject } from "@/api/projects";
import { useProjectStore } from "@/stores/project.store";
import { usePermissions } from "@/hooks/usePermissions";
import { getEffectivePipelineStatuses } from "@/lib/projectConfig";
import type { PipelineStatus, Project } from "@/api/types";

interface PipelineSectionProps {
  project: Project;
}

interface DraftStatus extends PipelineStatus {
  /** Local-only: stable ID for drag-and-drop while a row may be in flight
   *  (e.g. just-added, no server key yet). The server `key` is what we send. */
  _id: string;
}

export function PipelineSection({ project }: PipelineSectionProps) {
  const canEdit = usePermissions().canEditProject;
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const projects = useProjectStore((s) => s.projects);

  // Read the *resolved* list via the central helper so any future change
  // to fallback semantics happens in one place. Deep-clone so editing a
  // row doesn't mutate the store-held project.
  const initial = useMemo<DraftStatus[]>(() => {
    const effective = getEffectivePipelineStatuses(project);
    return structuredClone(effective)
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ ...s, _id: s.key }));
  }, [project]);

  const [drafts, setDrafts] = useState<DraftStatus[]>(initial);
  const [saving, setSaving] = useState(false);

  // Resync drafts only when the *content* of the effective list changes
  // (after a save, or an out-of-band project refresh). A bare reference
  // dep would also fire on every store touch, wiping mid-edit state.
  const initialKey = useMemo(
    () =>
      JSON.stringify(
        initial.map((s) => ({
          key: s.key,
          label: s.label,
          color: s.color ?? "gray",
          is_initial: !!s.is_initial,
          is_won: !!s.is_won,
          is_lost: !!s.is_lost,
        })),
      ),
    [initial],
  );
  useEffect(() => {
    setDrafts(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  const isDirty = useMemo(() => {
    if (drafts.length !== initial.length) return true;
    for (let i = 0; i < drafts.length; i++) {
      const a = drafts[i];
      const b = initial[i];
      if (
        a.key !== b.key ||
        a.label !== b.label ||
        a.color !== b.color ||
        !!a.is_initial !== !!b.is_initial ||
        !!a.is_won !== !!b.is_won ||
        !!a.is_lost !== !!b.is_lost
      ) {
        return true;
      }
    }
    return false;
  }, [drafts, initial]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    if (!canEdit) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = drafts.findIndex((s) => s._id === active.id);
    const newIndex = drafts.findIndex((s) => s._id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(drafts, oldIndex, newIndex);
    setDrafts(next);
  }

  function patchRow(_id: string, patch: Partial<DraftStatus>) {
    setDrafts((prev) =>
      prev.map((s) => (s._id === _id ? { ...s, ...patch } : s)),
    );
  }

  function setLabel(_id: string, label: string) {
    patchRow(_id, { label });
  }

  function setColor(_id: string, color: string) {
    patchRow(_id, { color });
  }

  function setWonFlag(_id: string, on: boolean) {
    setDrafts((prev) =>
      prev.map((s) => {
        if (s._id === _id) return { ...s, is_won: on, is_lost: on ? false : s.is_lost };
        if (on) return { ...s, is_won: false };
        return s;
      }),
    );
  }

  function setLostFlag(_id: string, on: boolean) {
    setDrafts((prev) =>
      prev.map((s) => {
        if (s._id === _id) return { ...s, is_lost: on, is_won: on ? false : s.is_won };
        if (on) return { ...s, is_lost: false };
        return s;
      }),
    );
  }

  function clearFlags(_id: string) {
    patchRow(_id, { is_won: false, is_lost: false });
  }

  function deleteRow(_id: string) {
    setDrafts((prev) => {
      const removed = prev.find((s) => s._id === _id);
      const remaining = prev.filter((s) => s._id !== _id);
      if (!removed?.is_initial || remaining.length === 0) {
        return remaining;
      }
      // Initial status was deleted — promote the first non-terminal
      // row to keep exactly one is_initial in the list (the backend
      // tolerates zero, but the UX expects "new leads land somewhere").
      const promoteIdx = remaining.findIndex((s) => !s.is_won && !s.is_lost);
      const targetIdx = promoteIdx >= 0 ? promoteIdx : 0;
      return remaining.map((s, i) =>
        i === targetIdx ? { ...s, is_initial: true } : s,
      );
    });
  }

  function addStage(label: string, color: string) {
    const trimmed = label.trim();
    if (!trimmed) return;
    const baseKey = slugify(trimmed);
    if (!baseKey) {
      toast.error("Pick a label with at least one letter or digit.");
      return;
    }
    let key = baseKey;
    let i = 2;
    const taken = new Set(drafts.map((s) => s.key));
    while (taken.has(key)) {
      key = `${baseKey}_${i++}`;
    }
    const order = drafts.length;
    setDrafts((prev) => [
      ...prev,
      {
        _id: `__new_${Date.now()}_${order}`,
        key,
        label: trimmed,
        color,
        order,
        is_initial: false,
        is_won: false,
        is_lost: false,
      },
    ]);
  }

  function reset() {
    setDrafts(initial);
  }

  async function save() {
    setSaving(true);
    try {
      const payload: PipelineStatus[] = drafts.map((s, i) => ({
        key: s.key,
        label: s.label.trim(),
        order: i,
        color: s.color ?? "gray",
        // is_initial wasn't sent before; without it the backend
        // defaulted it to false on every save, wiping the "where new
        // leads land" status. Now it round-trips along with the
        // won / lost flags.
        is_initial: !!s.is_initial,
        is_won: !!s.is_won,
        is_lost: !!s.is_lost,
      }));
      // Last-mile validation: empty labels can sneak in via inline edit.
      const invalid = payload.find((s) => s.label.length === 0);
      if (invalid) {
        toast.error("Every stage needs a label.");
        return;
      }
      await updateProject(project.slug, { pipeline_statuses: payload });
      // Refetch so we get the server-canonical `effective_pipeline_statuses`
      // (the PATCH response already includes it, but a fresh GET is the
      // cheapest way to be sure the rest of the app sees the same shape).
      const updated = await getProject(project.slug);
      setCurrentProject(updated);
      setProjects(
        projects.map((p) => (p.slug === updated.slug ? updated : p)),
      );
      toast.success("Pipeline updated");
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="space-y-5">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium tracking-tight text-slate-900">
            Pipeline stages
          </h2>
          <p className="text-sm text-slate-500">
            Define the stages your prospects go through.
          </p>
        </div>
        {canEdit && isDirty && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={reset}
              disabled={saving}
            >
              Discard
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void save()}
              disabled={saving || drafts.length === 0}
            >
              {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Save changes
            </Button>
          </div>
        )}
      </header>

      <div className="rounded-plane bg-white p-2 shadow-plane">
        {drafts.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400">
            No pipeline stages, add one below.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={drafts.map((s) => s._id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y divide-slate-900/[0.06]">
                {drafts.map((s) => (
                  <SortableRow
                    key={s._id}
                    status={s}
                    canEdit={canEdit}
                    onLabelChange={(v) => setLabel(s._id, v)}
                    onColorChange={(c) => setColor(s._id, c)}
                    onSetWon={() => setWonFlag(s._id, !s.is_won)}
                    onSetLost={() => setLostFlag(s._id, !s.is_lost)}
                    onClearFlags={() => clearFlags(s._id)}
                    onDelete={() => deleteRow(s._id)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}

        {canEdit && (
          <AddStageInline existingKeys={drafts.map((s) => s.key)} onAdd={addStage} />
        )}
      </div>

      <PipelinePreview drafts={drafts} />
    </section>
  );
}

// ─── Single sortable row ─────────────────────────────────────────────────────

interface SortableRowProps {
  status: DraftStatus;
  canEdit: boolean;
  onLabelChange: (value: string) => void;
  onColorChange: (color: string) => void;
  onSetWon: () => void;
  onSetLost: () => void;
  onClearFlags: () => void;
  onDelete: () => void;
}

function SortableRow({
  status,
  canEdit,
  onLabelChange,
  onColorChange,
  onSetWon,
  onSetLost,
  onClearFlags,
  onDelete,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status._id, disabled: !canEdit });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const colors = TAG_COLORS[status.color ?? "gray"] ?? TAG_COLORS.gray;
  const hasFlag = !!status.is_won || !!status.is_lost;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5",
        isDragging && "bg-slate-50 shadow-card-open ring-1 ring-slate-200",
      )}
    >
      <button
        type="button"
        className={cn(
          "shrink-0 text-slate-300 transition-colors",
          canEdit
            ? "cursor-grab active:cursor-grabbing hover:text-slate-500"
            : "cursor-not-allowed opacity-40",
        )}
        aria-label={`Drag ${status.label}`}
        {...(canEdit ? attributes : {})}
        {...(canEdit ? listeners : {})}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <ColorDotPicker
        color={status.color ?? "gray"}
        canEdit={canEdit}
        onPick={onColorChange}
      />

      <div className="min-w-0 flex-1">
        {canEdit ? (
          <Input
            value={status.label}
            maxLength={50}
            onChange={(e) => onLabelChange(e.target.value)}
            placeholder="Stage name"
            className="h-7 rounded-md border-transparent bg-transparent px-1 text-sm font-medium text-slate-900 shadow-none hover:border-slate-200 focus-visible:border-slate-300 focus-visible:bg-white focus-visible:ring-0"
          />
        ) : (
          <div className="px-1 text-sm font-medium text-slate-900">
            {status.label}
          </div>
        )}
        <div className="px-1 text-[11px] font-mono text-slate-400">
          {status.key}
        </div>
      </div>

      {status.is_won && (
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
          <Trophy className="h-3 w-3" />
          Won stage
        </span>
      )}
      {status.is_lost && (
        <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
          <XCircle className="h-3 w-3" />
          Lost stage
        </span>
      )}
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium",
          colors.pillBg,
          colors.pillText,
          colors.pillBorder,
        )}
      >
        <span className={cn("h-1.5 w-1.5 rounded-full", colors.dot)} />
        Preview
      </span>

      {canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-500"
              aria-label={`Actions for ${status.label}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={onSetWon}>
              <Trophy className="mr-2 h-3.5 w-3.5" />
              {status.is_won ? "Remove Won flag" : "Set as Won stage"}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onSetLost}>
              <XCircle className="mr-2 h-3.5 w-3.5" />
              {status.is_lost ? "Remove Lost flag" : "Set as Lost stage"}
            </DropdownMenuItem>
            {hasFlag && (
              <DropdownMenuItem onSelect={onClearFlags}>
                <X className="mr-2 h-3.5 w-3.5" />
                Remove Won/Lost flag
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
              onSelect={onDelete}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
}

// ─── Color dot + popover ─────────────────────────────────────────────────────

interface ColorDotPickerProps {
  color: string;
  canEdit: boolean;
  onPick: (color: string) => void;
}

function ColorDotPicker({ color, canEdit, onPick }: ColorDotPickerProps) {
  const [open, setOpen] = useState(false);
  const colors = TAG_COLORS[color] ?? TAG_COLORS.gray;

  if (!canEdit) {
    return (
      <span
        className={cn(
          "h-4 w-4 shrink-0 rounded-full ring-1 ring-inset ring-black/5",
          colors.swatch,
        )}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Change color"
          // pointerdown stop-propagation: dnd-kit's PointerSensor would
          // otherwise consider this gesture as the start of a row drag and
          // suppress the click that opens the popover.
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            "h-4 w-4 shrink-0 cursor-pointer rounded-full ring-1 ring-inset ring-black/5 transition-all",
            "hover:scale-110 hover:ring-2 hover:ring-slate-300 hover:ring-offset-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1",
            colors.swatch,
          )}
        />
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-auto p-3">
        <div className="grid grid-cols-9 gap-1.5">
          {TAG_COLOR_KEYS.map((c) => {
            const cs = TAG_COLORS[c];
            const isSelected = c === color;
            return (
              <button
                key={c}
                type="button"
                aria-label={c}
                onClick={() => {
                  onPick(c);
                  setOpen(false);
                }}
                className={cn(
                  "h-6 w-6 rounded-full transition-transform hover:scale-110",
                  cs.swatch,
                  isSelected && "ring-2 ring-slate-900 ring-offset-2",
                )}
              />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Add new stage inline ───────────────────────────────────────────────────

interface AddStageInlineProps {
  existingKeys: string[];
  onAdd: (label: string, color: string) => void;
}

function AddStageInline({ onAdd }: AddStageInlineProps) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState<string>("indigo");

  function close() {
    setOpen(false);
    setLabel("");
    setColor("indigo");
  }

  function commit() {
    onAdd(label, color);
    close();
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
      >
        <Plus className="h-3.5 w-3.5" />
        Add stage
      </button>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-dashed border-indigo-200 bg-indigo-50/30 px-3 py-2.5">
      <ColorDotPicker color={color} canEdit onPick={setColor} />
      <Input
        autoFocus
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") close();
        }}
        placeholder="Stage name"
        maxLength={50}
        className="h-8 flex-1 rounded-md border-slate-200 bg-white text-sm"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={close}
      >
        Cancel
      </Button>
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={commit}
        disabled={label.trim().length === 0}
      >
        Add
      </Button>
    </div>
  );
}

// ─── Pipeline preview ───────────────────────────────────────────────────────

function PipelinePreview({ drafts }: { drafts: DraftStatus[] }) {
  const flow = drafts.filter((s) => !s.is_lost);
  const lost = drafts.filter((s) => s.is_lost);

  if (drafts.length === 0) return null;

  return (
    <div className="rounded-plane bg-white p-5 shadow-plane">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        Preview
      </h3>
      <div className="flex flex-wrap items-center gap-y-3">
        {flow.map((s, i) => {
          const colors = TAG_COLORS[s.color ?? "gray"] ?? TAG_COLORS.gray;
          const isLast = i === flow.length - 1;
          return (
            <Fragment key={s._id}>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                  colors.pillBg,
                  colors.pillText,
                  colors.pillBorder,
                )}
              >
                <span className={cn("h-1.5 w-1.5 rounded-full", colors.dot)} />
                {s.label || s.key}
                {s.is_won && <Trophy className="ml-0.5 h-3 w-3" />}
              </span>
              {!isLast && (
                <ChevronRight className="mx-1.5 h-3 w-3 text-slate-300" />
              )}
            </Fragment>
          );
        })}
      </div>
      {lost.length > 0 && (
        <div className="mt-4 flex items-start gap-3 border-t border-dashed border-slate-200 pt-3">
          <ArrowDown className="mt-1 h-3 w-3 text-slate-300" />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-400">
              Off-flow
            </span>
            <ArrowRight className="h-3 w-3 text-slate-300" />
            {lost.map((s) => {
              const colors = TAG_COLORS[s.color ?? "gray"] ?? TAG_COLORS.gray;
              return (
                <span
                  key={s._id}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                    colors.pillBg,
                    colors.pillText,
                    colors.pillBorder,
                  )}
                >
                  <span
                    className={cn("h-1.5 w-1.5 rounded-full", colors.dot)}
                  />
                  {s.label || s.key}
                  <XCircle className="ml-0.5 h-3 w-3" />
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function slugify(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip diacritics
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 30);
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 422) {
      const detail = (err.response?.data as { detail?: unknown } | undefined)
        ?.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0] as { msg?: string };
        if (first?.msg) return first.msg;
      }
      return "Some companies use a status you tried to remove. Move them first.";
    }
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't save the pipeline.";
}
